import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import { Link } from "react-router-dom";
import { Pagination } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

interface AdminListing {
  id: string;
  displayTitle: string;
  price: number | null;
  condition: string;
  conditionLabel: string;
  status: string;
  language: string | null;
  sellerName: string;
  gameTitle: string | null;
  createdAt: string;
}

interface AdminListingsResult {
  listings: AdminListing[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

const ADMIN_LISTINGS_QUERY = gql`
  query AdminListings($page: Int, $perPage: Int, $query: String, $status: String) {
    adminListings(page: $page, perPage: $perPage, query: $query, status: $status) {
      listings {
        id
        displayTitle
        price
        conditionLabel
        status
        language
        sellerName
        createdAt
      }
      totalCount
      totalPages
      currentPage
    }
  }
`;

const STATUS_OPTIONS = [
  { value: "", label: "Visi" },
  { value: "active", label: "Aktyvūs" },
  { value: "reserved", label: "Rezervuoti" },
  { value: "sold", label: "Parduoti" },
  { value: "cancelled", label: "Atšaukti" },
];

const statusVariant = (status: string) => {
  switch (status) {
    case "active":
      return "success";
    case "reserved":
      return "warning";
    case "sold":
      return "info";
    case "cancelled":
      return "danger";
    default:
      return "default";
  }
};

const statusLabel = (status: string) => {
  switch (status) {
    case "active":
      return "Aktyvus";
    case "reserved":
      return "Rezervuotas";
    case "sold":
      return "Parduotas";
    case "cancelled":
      return "Atšauktas";
    default:
      return status;
  }
};

export function AdminListingsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout((window as any).__adminListingsSearchTimeout);
    (window as any).__adminListingsSearchTimeout = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["adminListings", page, debouncedSearch, statusFilter],
    queryFn: () =>
      graphqlClient.request<{ adminListings: AdminListingsResult }>(
        ADMIN_LISTINGS_QUERY,
        {
          page,
          perPage: 25,
          query: debouncedSearch || undefined,
          status: statusFilter || undefined,
        }
      ),
  });

  const result = data?.adminListings;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("lt-LT");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Admin valdymo skydas
      </Link>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skelbimai</h1>
          {result && (
            <p className="mt-1 text-sm text-gray-500">
              Iš viso: {result.totalCount}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="input"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Ieškoti pagal pavadinimą..."
            className="input w-72"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : result && result.listings.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Pavadinimas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Kaina
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Būklė
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Statusas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Pardavėjas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Sukurta
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {result.listings.map((listing) => (
                  <tr key={listing.id}>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      <Link
                        to={`/listings/${listing.id}`}
                        className="hover:text-primary-600"
                      >
                        {listing.displayTitle}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {listing.price != null
                        ? `${listing.price.toFixed(2)} €`
                        : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {listing.conditionLabel}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <Badge variant={statusVariant(listing.status)}>
                        {statusLabel(listing.status)}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {listing.sellerName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(listing.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={result.currentPage}
              totalPages={result.totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">Skelbimų nerasta</p>
        </div>
      )}
    </div>
  );
}
